/**
 * The ONE attribution block every generated embed snippet emits.
 *
 * WHY THIS IS A MODULE AND NOT THREE STRING LITERALS.
 *
 * This markup is pasted into a THIRD-PARTY page's own DOM — it sits outside the
 * iframe, so unlike the widget's internal links it is fully attributable to the
 * host document. Until 2026-08-21 all three snippets emitted
 * `<a href="https://ishvaram.com/panchang/" rel="noopener">Ishvaram Vedic
 * Astrology</a>` and its siblings: a FOLLOWED, keyword-rich anchor pointing at
 * a keyword-targeted page, replicated across every site that installed a
 * widget. That is the textbook example Google's link-spam guidance gives for
 * embeddable-widget link schemes, and its stated remedy is nofollow. We are
 * under an active algorithmic spam classification (ADR-0091), where that
 * footprint is the difference between an algorithmic re-score and a durable
 * one.
 *
 * The three copies were identical in shape and drifted only in their label and
 * href, which is exactly how the defect reached all three at once. One owner
 * means the next widget cannot reintroduce it, and
 * `tests/embed-attribution-link-policy.test.ts` fails if any embed surface
 * grows a followed link.
 *
 * The rules encoded here (see docs/reference/historical-ishvaram/embeddable-panchang-widget-spec-2026-08.md §1):
 *   1. `rel="nofollow noopener"` — always, no configuration removes it.
 *   2. The anchor is the BRAND NAME ONLY. Never a keyword phrase, never a city.
 *   3. The href is the homepage, never a keyword-targeted deep page.
 *   4. One link per snippet.
 * The descriptive prefix ("Panchang data by") stays plain text on the host
 * page: it is honest attribution and carries no link equity.
 */
import { EMBED_ATTRIBUTION_COLOR } from "./embed-snippet-style";

/** Anchor text for every embed attribution link. Brand only — see constraint 2. */
export const EMBED_ATTRIBUTION_ANCHOR = "Ishvaram";

/** Link target for every embed attribution link. Homepage only — see constraint 3. */
export const EMBED_ATTRIBUTION_HREF = "https://ishvaram.com/";

/** `rel` for every link an embed surface emits, inside the iframe or outside. */
export const EMBED_LINK_REL = "nofollow noopener";

/**
 * The attribution line appended under a generated embed iframe.
 *
 * @param dataLabel what the widget provides, e.g. "Panchang data". Rendered as
 *   unlinked text so the credit reads naturally without becoming an anchor.
 */
export function embedAttributionHtml(dataLabel: string): string {
  return `<div style="font-size: 11px; text-align: center; margin-top: 6px; color: ${EMBED_ATTRIBUTION_COLOR};">
  ${dataLabel} by <a href="${EMBED_ATTRIBUTION_HREF}" target="_blank" rel="${EMBED_LINK_REL}" style="color: ${EMBED_ATTRIBUTION_COLOR}; text-decoration: underline;">${EMBED_ATTRIBUTION_ANCHOR}</a>
</div>`;
}
